import React, { createContext, useContext, useState, ReactNode } from 'react';

type ThemeType = 'light' | 'dark';

interface ThemeContextType {
	theme: ThemeType;
	toggleTheme: () => void;
	themeOverride: ThemeType | null;
	setThemeOverride: (override: ThemeType | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface LocalThemeProviderProps {
	children?: ReactNode;
}

export const LocalThemeProvider: React.FC<LocalThemeProviderProps> = ({
	children,
}) => {
	// Check localStorage for saved theme, default to 'dark' if not found
	const storedTheme = localStorage.getItem('theme') as ThemeType | null;
	const [theme, setTheme] = useState<ThemeType>(storedTheme || 'dark');
	// Temporary forced theme (e.g. quiz play is dark-only); the user's saved
	// preference stays untouched and wins again once the override clears.
	const [themeOverride, setThemeOverride] = useState<ThemeType | null>(null);

	const toggleTheme = () => {
		const newTheme = theme === 'light' ? 'dark' : 'light';
		setTheme(newTheme);
		localStorage.setItem('theme', newTheme); // Save the new theme to localStorage
	};

	return (
		<ThemeContext.Provider
			value={{ theme, toggleTheme, themeOverride, setThemeOverride }}
		>
			{children}
		</ThemeContext.Provider>
	);
};

export const useThemeContext = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useThemeContext must be used within a ThemeProvider');
	}
	return context;
};
